import { useEffect, useRef, useCallback } from "react";
import { VirtuosoHandle } from "react-virtuoso";

interface UseScrollStateOptions<T> {
  storageKey: string;
  isLoading: boolean;
  items: T[];
  highlightedItemId?: string | null;
  getItemId: (item: T) => string;
  isFetchingMore?: boolean;
  resetTrigger?: any;
  restoreDelay?: number;
  highlightScrollBehavior?: "auto" | "smooth";
}

interface ScrollStateReturn {
  saveScrollPosition: () => void;
  clearSavedPosition: () => void;
  scrollToItem: (itemId: string, align?: "start" | "center" | "end") => void;
}

export function useScrollState<T>(
  virtuosoRef: React.RefObject<VirtuosoHandle>,
  options: UseScrollStateOptions<T>
): ScrollStateReturn {
  const {
    storageKey,
    isLoading,
    items,
    highlightedItemId,
    getItemId,
    isFetchingMore = false,
    resetTrigger,
    restoreDelay = 150,
    highlightScrollBehavior = "auto",
  } = options;

  const hasTriedRestore = useRef(false);
  const lastHighlightedItemIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoading && items.length > 0 && !hasTriedRestore.current) {
      hasTriedRestore.current = true;

      const savedScrollState = sessionStorage.getItem(storageKey);
      if (savedScrollState && virtuosoRef.current) {
        try {
          const { scrollTop } = JSON.parse(savedScrollState);
          if (typeof scrollTop === "number" && scrollTop >= 0) {
            setTimeout(() => {
              virtuosoRef.current?.scrollTo({
                top: scrollTop,
                behavior: "auto",
              });
            }, restoreDelay);
          }
        } catch (e) {
          console.error("Failed to restore scroll position:", e);
        }
        sessionStorage.removeItem(storageKey);
      }
    }
  }, [isLoading, items.length, storageKey, restoreDelay, virtuosoRef]);

  useEffect(() => {
    if (resetTrigger !== undefined) {
      hasTriedRestore.current = false;
    }
  }, [resetTrigger]);

  useEffect(() => {
    const highlightedItemChanged =
      highlightedItemId !== lastHighlightedItemIdRef.current;
    const hasHighlightedItem = highlightedItemId && items.length > 0;
    const isNotFetchingMore = !isFetchingMore;

    if (hasHighlightedItem && isNotFetchingMore && highlightedItemChanged) {
      const highlightedIndex = items.findIndex(
        (item) => getItemId(item) === highlightedItemId
      );

      if (highlightedIndex >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: highlightedIndex,
          align: "center",
          behavior: highlightScrollBehavior,
        });
      }
    }

    lastHighlightedItemIdRef.current = highlightedItemId || null;
  }, [
    highlightedItemId,
    items,
    isFetchingMore,
    getItemId,
    highlightScrollBehavior,
    virtuosoRef,
  ]);

  const saveScrollPosition = useCallback((): void => {
    if (virtuosoRef.current) {
      virtuosoRef.current.getState((state) => {
        if (state && typeof state.scrollTop === "number") {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({ scrollTop: state.scrollTop })
          );
        }
      });
    }
  }, [storageKey, virtuosoRef]);

  const clearSavedPosition = useCallback((): void => {
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  const scrollToItem = useCallback(
    (itemId: string, align: "start" | "center" | "end" = "center"): void => {
      const itemIndex = items.findIndex((item) => getItemId(item) === itemId);
      if (itemIndex >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: itemIndex,
          align,
          behavior: "smooth",
        });
      }
    },
    [items, getItemId, virtuosoRef]
  );

  return {
    saveScrollPosition,
    clearSavedPosition,
    scrollToItem,
  };
}
